import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import ExperienceCompactSearchTile from '../ExperienceTiles/ExperienceCompactSearchTile'
import ExperienceCompactSearchTileSkeleton from '../ExperienceTiles/ExperienceCompactSearchTileSkeleton'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { fillArray } from 'lib/array/arrayUtils'
import useAttractionOffers from 'hooks/Experiences/useAttractionOffers'
import { fetchExperiencesById } from 'actions/ExperienceActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useExperiencesSortOptions from '../hooks/useExperiencesSortOptions'
import ExperienceSearchPageSort from './ExperienceSearchPageSort'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { getAttractionAttribution, setAttractionAttribution } from 'lib/attractions/attractionAttribution'
import { trackClientEvent } from 'analytics/analytics'

const ListGrid = styled.div`
  display: flex;
  flex-direction: column;
  min-height: ${rem(200)};
  position: relative;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${rem(300)}, 1fr));
    gap: ${rem(36)} ${rem(16)};
  }
`

interface Props {
  attractionId: string
  attractionName?: string
  destination?: string
  searchFilters?: Experiences.SearchFilters
  /** Skip selected-attraction offer fetching when the destination list has fallen back to another place. */
  suppressResults?: boolean
  onOfferIdsStateChange?: (state: { offerIds: Array<string>; resolved: boolean }) => void
  onClearAttraction?: () => void
}

function ExperienceSearchAttractionResults({ attractionId, attractionName, destination, searchFilters, suppressResults, onOfferIdsStateChange, onClearAttraction }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { sortBy } = useExperiencesSortOptions()
  const attractionOffersId = suppressResults ? undefined : attractionId
  const { offerIds, fetching: fetchingOffers, error: attractionError } = useAttractionOffers(attractionOffersId, searchFilters)
  const suppressionTrackedRef = useRef(false)

  const experiencesMap = useAppSelector(state => state.experience.experiences)
  const fetchingExperiencesMap = useAppSelector(state => state.experience.fetchingExperiences)
  const experienceErrorsMap = useAppSelector(state => state.experience.experienceErrors)

  // attractionId is a slug (e.g. "sydney-opera-house"), no spaces or dashes sequences
  // that would break the dbt REGEXP_EXTRACT(r'Attraction Offers - (.+)$') extraction
  const tracking = useOfferListTracking(
    getListName('OfferList', 'EXPERIENCES', `Attraction Offers - ${attractionId}`),
    {
      source: 'attraction',
      key: attractionId,
    },
  )

  const handleOfferClick = useCallback(() => {
    const existing = getAttractionAttribution()
    const name = (existing?.id === attractionId ? existing.name : null) ?? attractionName ?? attractionId
    const dest = (existing?.id === attractionId ? existing.destination : null) ?? destination ?? ''
    setAttractionAttribution(attractionId, name, dest)
  }, [attractionId, attractionName, destination])

  useEffect(() => {
    if (offerIds.length > 0) {
      dispatch(fetchExperiencesById(...offerIds))
    }
  }, [offerIds, dispatch])

  const experiences = useMemo(() => {
    const loaded = offerIds.map(id => experiencesMap[id]).filter(Boolean)
    switch (sortBy) {
      case 'price.asc': return [...loaded].sort((a, b) => a.price - b.price)
      case 'price.desc': return [...loaded].sort((a, b) => b.price - a.price)
      case 'customerRating': return [...loaded].sort((a, b) => (b.rating?.score ?? 0) - (a.rating?.score ?? 0))
      case 'bestValue': return [...loaded].sort((a, b) => b.discount - a.discount)
      default: return loaded
    }
  }, [offerIds, experiencesMap, sortBy])

  const fetchingExperiences = useMemo(
    () => offerIds.some(id => fetchingExperiencesMap[id]),
    [offerIds, fetchingExperiencesMap],
  )

  const allExperiencesResolved = useMemo(
    () => offerIds.length > 0 && offerIds.every(id => experiencesMap[id] || experienceErrorsMap[id] || (id in fetchingExperiencesMap && !fetchingExperiencesMap[id])),
    [offerIds, experiencesMap, experienceErrorsMap, fetchingExperiencesMap],
  )

  useEffect(() => {
    if (suppressResults) {
      if (!suppressionTrackedRef.current) {
        trackClientEvent({
          subject: attractionId,
          action: 'suppressed_by_destination_fallback',
          category: 'experience_selected_attraction_results',
          type: 'operational',
        })
        suppressionTrackedRef.current = true
      }

      onOfferIdsStateChange?.({
        offerIds: [],
        resolved: true,
      })
      return
    }

    suppressionTrackedRef.current = false
    onOfferIdsStateChange?.({
      offerIds,
      resolved: !!attractionError || (!fetchingOffers && (offerIds.length === 0 || allExperiencesResolved)),
    })
  }, [allExperiencesResolved, attractionError, attractionId, fetchingOffers, offerIds, onOfferIdsStateChange, suppressResults])

  const hasError = !!attractionError
  const fetching = !hasError && (fetchingOffers || fetchingExperiences || (offerIds.length > 0 && !allExperiencesResolved))
  const hasNoOffers = suppressResults || (!hasError && !fetching && offerIds.length === 0)

  return (
    <VerticalSpacer gap={16}>
      {hasError && <BodyText variant="large" colour="neutral-two">
        {intl.formatMessage({
          id: 'qawEZZcZ5gPQ8RUSbqD9',
          defaultMessage: 'Something went wrong. Please try again.',
          description: 'Error message shown when loading attraction offers fails',
        })}
      </BodyText>}
      {hasNoOffers && <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">
          {intl.formatMessage({
            id: 'Lvvq358fd0gTgldT2QFE',
            defaultMessage: 'No experiences match this attraction with your current filters.',
            description: 'Heading shown when an attraction has no offers matching the active filters',
          })}
        </BodyText>
        <BodyText variant="medium" colour="neutral-two">
          {intl.formatMessage({
            id: '72Y-W3ztzqpOR1Sd6kMY',
            defaultMessage: 'Clear the selected attraction to keep browsing this destination with the same filters.',
            description: 'Suggestion shown when an attraction has no offers matching the active filters',
          })}
        </BodyText>
        {onClearAttraction && <div>
          <TextButton kind="tertiary" size="small" onClick={onClearAttraction} horizontalOutdent="start">
            {intl.formatMessage({
              id: '7ONIz1h0jPll9TX1Rooi',
              defaultMessage: 'Clear selected attraction',
              description: 'Action label to deselect the current attraction while keeping search filters',
            })}
          </TextButton>
        </div>}
      </VerticalSpacer>}
      {!hasError && !fetching && !hasNoOffers && <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
        <BodyText variant="large" colour="neutral-two">
          {intl.formatMessage(
            {
              id: '3srKgtYjSLwOR-04HAjP',
              defaultMessage: '{count, plural, one {# experience} other {# experiences}} found',
              description: 'Pluralized label showing the number of experiences returned in search results',
            },
            { count: experiences.length },
          )}
        </BodyText>
        <ExperienceSearchPageSort />
      </Group>}
      {!hasNoOffers && <OfferListEventsProvider tracking={tracking}>
        <ListGrid onClickCapture={handleOfferClick}>
          {fetching && fillArray(4).map((id) => <ExperienceCompactSearchTileSkeleton key={id} />)}
          {!fetching && !hasError && experiences.map((experience, index) => (
            <ExperienceCompactSearchTile
              key={experience.id}
              experience={experience}
              position={index}
            />
          ))}
        </ListGrid>
      </OfferListEventsProvider>}
    </VerticalSpacer>
  )
}

export default ExperienceSearchAttractionResults
